// src/lib/haptics/hapticMap.ts
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

const isNative = Capacitor.isNativePlatform();

function userEnabled(): boolean {
  try {
    return localStorage.getItem('helios-haptics-enabled') !== 'false';
  } catch {
    return true;
  }
}

export function hapticsAvailable(): boolean {
  if (isNative) return true;
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
}

// Map raw ms duration → iOS impact intensity. Web vibrate accepts arbitrary
// durations; iOS only has Light/Medium/Heavy. Threshold: ≤20 light, ≤60 medium, else heavy.
function msToImpact(ms: number): ImpactStyle {
  if (ms <= 20) return ImpactStyle.Light;
  if (ms <= 60) return ImpactStyle.Medium;
  return ImpactStyle.Heavy;
}

// Raw pattern fire. Web: navigator.vibrate. Native: Haptics.impact mapped from first ms.
// Multi-pulse cadence is ignored on native (iOS has no pattern API) — pick a named
// pattern via haptic() if you need rich cascade behavior.
export function triggerHaptic(pattern: number | number[]): void {
  try {
    if (!hapticsAvailable() || !userEnabled()) return;
    if (isNative) {
      const ms = Array.isArray(pattern) ? pattern[0] : pattern;
      void Haptics.impact({ style: msToImpact(ms) });
      return;
    }
    navigator.vibrate(pattern);
  } catch {
    // Silent — haptics are non-critical.
  }
}

export function stopHaptic(): void {
  try {
    if (!isNative && typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(0);
    }
  } catch {
    /* silent */
  }
}

// Named haptic patterns. Web ms cadence preserved for vibrate fallback.
export const HapticPatterns = {
  buttonTap:         [20],
  setLogged:         [30],
  setCompleted:      [30],
  prAchieved:        [50, 100, 50],
  achievementUnlock: [30, 30, 60],
  leaderboardRank:   [16],
  coachMessage:      [16],
  workoutComplete:   [50],
  streakMilestone:   [30, 30, 60],
  streakBroken:      [100, 50, 100],
  injuryAlert:       [80, 40, 80],
  missedTarget:      [80, 40, 80],
  actionFailed:      [60, 30, 60],
  rankUp:            [50, 50, 100],
  modalDismiss:      [30],
  formSubmit:        [20],
  skillUnlock:       [50, 100, 100, 150, 200],
  breathingSync:     [16],
} as const;

export type HapticPatternName = keyof typeof HapticPatterns;

// Native-rich mapping for named patterns — uses notification types where the intent
// is achievement/warning/error rather than a generic impact. iOS UI guidelines:
// notification types are reserved for semantic events (success/warning/error),
// impact for tactile UI feedback (button presses, toggle flips).
async function fireNativeNamed(name: HapticPatternName): Promise<void> {
  switch (name) {
    case 'buttonTap':
    case 'leaderboardRank':
    case 'coachMessage':
    case 'modalDismiss':
    case 'formSubmit':
    case 'breathingSync':
      await Haptics.impact({ style: ImpactStyle.Light });
      return;
    case 'setLogged':
    case 'setCompleted':
      await Haptics.impact({ style: ImpactStyle.Medium });
      return;
    case 'workoutComplete':
      await Haptics.impact({ style: ImpactStyle.Heavy });
      return;
    case 'prAchieved':
    case 'achievementUnlock':
    case 'streakMilestone':
    case 'rankUp':
      await Haptics.notification({ type: NotificationType.Success });
      return;
    case 'streakBroken':
    case 'missedTarget':
      await Haptics.notification({ type: NotificationType.Warning });
      return;
    case 'injuryAlert':
    case 'actionFailed':
      await Haptics.notification({ type: NotificationType.Error });
      return;
    case 'skillUnlock': {
      // Escalating cascade — three impacts ramping into a success crescendo.
      await Haptics.impact({ style: ImpactStyle.Light });
      await new Promise((r) => setTimeout(r, 100));
      await Haptics.impact({ style: ImpactStyle.Medium });
      await new Promise((r) => setTimeout(r, 150));
      await Haptics.impact({ style: ImpactStyle.Heavy });
      await new Promise((r) => setTimeout(r, 200));
      await Haptics.notification({ type: NotificationType.Success });
      return;
    }
  }
}

export function haptic(name: HapticPatternName): void {
  try {
    if (!userEnabled()) return;
    if (isNative) {
      void fireNativeNamed(name);
      return;
    }
    if (!hapticsAvailable()) return;
    navigator.vibrate([...HapticPatterns[name]]);
  } catch {
    /* silent */
  }
}
